/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { Grid } from '@radix-ui/themes'
import { useGetChapter } from '@/services/chapterService'
import { useUpdateQuestion } from '@/services/updateQuestionService'
import {
  MarkingSchemeType,
  QuestionDetailType,
  SubQuestion,
  UpdateQuestionBodyType,
  UpdateSubQuestion,
} from '@/types/question.type'
import { toast } from 'sonner'
import { useModulesStore } from '@/stores/useModulesStore'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { buildSingleSubQuestionText } from '@/utils/question-text'
import QuestionHeader from '../QuestionHeader'
import { ChildQuestionContainer } from '../QuestionItemContainer'
import QuestionSchema from '../QuestionSchema'

interface ParagraphQuestionDetailProps {
  questionDetail: QuestionDetailType
  isLoading: boolean
  questionType: string
  isCaseStudy?: boolean
  fromClass: boolean
  refetchQuestionDetail: () => void
  onDeleteSuccess?: () => void
}

const ParagraphQuestionDetail = ({
  fromClass,
  questionDetail,
  isLoading,
  questionType,
  isCaseStudy = false,
  refetchQuestionDetail,
  onDeleteSuccess,
}: ParagraphQuestionDetailProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editableData, setEditableData] = useState<QuestionDetailType | null>(
    null
  )

  const { updateQuestionAsync, isUpdating } = useUpdateQuestion()
  const { selectedModule } = useModulesStore()

  const { chapters } = useGetChapter({
    module_id: String(selectedModule?.id ?? ''),
    page: 1,
    limit: 100,
    search: '',
  })

  const isBlank = (value?: string) => (value ?? '').trim().length === 0

  const isInvalid = useMemo(() => {
    if (!isEditing) return false
    if (!editableData) return true
    const q = editableData
    // For questions with only one sub-question, validate the sub-question text instead of main question text
    if (q.sub_questions?.length === 1) {
      if (
        isBlank(q.sub_questions[0].text) ||
        isBlank(q.sub_questions[0].indicative_content)
      ) {
        return true
      }
    } else {
      // For questions with multiple sub-questions, validate the main question text
      if (isBlank(q.text)) {
        return true
      }
    }
    for (const sub of q.sub_questions) {
      // Validate indicative content per sub-question (now stored at sub level)
      if (isBlank(sub.indicative_content)) {
        return true
      }
      // Validate time limit
      if (!sub.time_limit || sub.time_limit < 1) {
        return true
      }

      // Validate marks
      if (!sub.marks || sub.marks < 1) {
        return true
      }

      const scheme = sub.marking_scheme || []
      const isEntryBlank = (s: { mark_range?: string; description?: string }) =>
        isBlank(s.mark_range) && isBlank(s.description)
      const isSchemeEntirelyBlank =
        scheme.length === 0 || scheme.every((s) => isEntryBlank(s))

      // If marking scheme is entirely blank, only validate that marks >= 1 (already done above)
      if (isSchemeEntirelyBlank) {
        continue
      }

      // Consider only non-blank entries for validation
      const activeLevels = scheme.filter((s) => !isEntryBlank(s))

      // If no active levels after filtering, treat as entirely blank
      if (activeLevels.length === 0) {
        continue
      }

      // Mark range is now optional - no validation needed

      // Mark range validation is now optional - only validate if provided
      const sortedActive = [...activeLevels]
        .filter((s) => !isBlank(s.mark_range)) // Only validate levels with mark ranges
        .sort((a, b) => (a.level || 0) - (b.level || 0))

      if (sortedActive.length > 0) {
        const last = sortedActive[sortedActive.length - 1]
        const value = (last.mark_range || '').trim()
        let maxAllowed = 0
        // Only allow min-max format, not single numbers
        const parts = value.split('-')
        if (parts.length === 2) {
          const maxStr = parts[1].trim()
          const minStr = parts[0].trim()
          if (/^\d+$/.test(minStr) && /^\d+$/.test(maxStr)) {
            const minVal = parseInt(minStr, 10)
            const maxVal = parseInt(maxStr, 10)
            if (minVal <= maxVal) {
              maxAllowed = maxVal
            }
          }
        }

        // Only check marks against marking scheme if mark range is provided and valid
        if (maxAllowed > 0 && sub.marks !== maxAllowed) {
          return true
        }
      }
    }
    return false
  }, [editableData, isEditing])

  const handleSave = () => {
    if (!editableData) return

    if (isInvalid) {
      const firstInvalid = document.querySelector(
        '[data-invalid="true"], [aria-invalid="true"]'
      ) as HTMLTextAreaElement | HTMLInputElement | null
      const container = (firstInvalid?.closest('[data-question-container]') ||
        firstInvalid?.closest('[data-scroll-target]')) as HTMLElement | null
      const target: HTMLElement | null = container || firstInvalid
      if (target) {
        const trigger = target.querySelector(
          '[data-collapsible-trigger="true"]'
        ) as HTMLElement | null
        if (trigger) trigger.click()
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setTimeout(() => {
          if (firstInvalid && typeof firstInvalid.focus === 'function') {
            firstInvalid.focus()
          }
        }, 150)
      }
      toast.error('Please fix the highlighted fields before saving.', {
        duration: 3000,
        position: 'top-right',
      })
      return
    }

    const subQuestionsPayload: UpdateSubQuestion[] =
      editableData.sub_questions.map((subQuestion) => ({
        indicative_content: subQuestion.indicative_content,
        id: subQuestion.sub_question_id,
        mark: subQuestion.marks,
        text: subQuestion.text,
        time_required: subQuestion.time_limit,
        chapter_id: subQuestion.chapter_id ?? undefined,
        options: (subQuestion.options || []).map(
          (opt: {
            option_id?: number
            id?: number
            text?: string
            option_text?: string
            is_correct?: boolean
          }) => ({
            id: opt.option_id ?? opt.id ?? 0,
            option_text: (opt.text ?? opt.option_text ?? '').toString(),
            is_correct: opt.is_correct,
          })
        ),
        update_marking_scheme: subQuestion.marking_scheme
          .filter((scheme) => {
            // Keep any entry that has mark_range, description, or rubric
            const hasMarkRange = (scheme.mark_range ?? '').trim().length > 0
            const hasDescription = (scheme.description ?? '').trim().length > 0
            const hasRubric = (scheme.rubric ?? '').trim().length > 0
            return hasMarkRange || hasDescription || hasRubric
          })
          .map((scheme) => {
            const result: any = {
              level: null,
              min_mark: null,
              max_mark: null,
              descriptor: scheme.description,
              rubric: scheme.rubric,
            }

            // Only set level and mark values if mark_range is provided
            if (scheme.mark_range && scheme.mark_range.trim().length > 0) {
              result.level = scheme.level
              const parts = scheme.mark_range.split('-')
              if (parts.length === 2) {
                result.min_mark = parseFloat(parts[0] ?? '0') || 0
                result.max_mark = parseFloat(parts[1] ?? '0') || 0
              }
            }

            if (scheme.marking_scheme_level_id) {
              result.id = scheme.marking_scheme_level_id
            }

            return result
          }),
      }))

    const reqBody: UpdateQuestionBodyType = {
      // Always the question's own text. Substituting the sub-question's text when there
      // is exactly one part overwrote the scenario with the task on every save.
      text: editableData.text,

      sub_questions: subQuestionsPayload,
    }
    console.log('Request Body:', reqBody)

    updateQuestionAsync({
      questionId: questionDetail.question_id.toString(),
      data: reqBody,
    })
      .then(() => {
        toast.success('Update question successfully', {
          duration: 3000,
          position: 'top-right',
        })
        refetchQuestionDetail()
        setIsEditing(false)
      })
      .catch((error: AxiosError) => {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  const handleCancel = () => {
    // Reset editableData to the original questionDetail
    setEditableData(
      JSON.parse(JSON.stringify(questionDetail)) as QuestionDetailType
    )
    setIsEditing(false)
  }

  const cantEditMark = useMemo(() => {
    if (!questionDetail) return true

    return questionDetail.in_pt
  }, [questionDetail])

  useEffect(() => {
    if (questionDetail && !editableData) {
      setEditableData(
        JSON.parse(JSON.stringify(questionDetail)) as QuestionDetailType
      )
    }
  }, [questionDetail])

  if (isLoading) {
    return (
      <Grid columns={'1'} gap={'4'} className='mt-3'>
        <Skeleton className='h-[200px] w-full bg-[#d6d6d6]' />
        <Skeleton className='h-[200px] w-full bg-[#d6d6d6]' />
      </Grid>
    )
  }

  return (
    <>
      <QuestionHeader
        section={questionDetail?.section ?? ''}
        isInDetail={true}
        questionInfo={questionDetail.question_info ?? ''}
        isUpdating={isUpdating}
        cantEdit={!fromClass}
        isEditing={isEditing}
        isCaseStudy={isCaseStudy}
        setIsEditing={setIsEditing}
        handleSave={handleSave}
        handleCancel={handleCancel}
        saveDisabled={isUpdating}
        question_id={questionDetail.question_id}
        questionType={questionType}
        createdAt={questionDetail.created_at ?? ''}
        in_pt={questionDetail.in_pt}
        onDeleteSuccess={onDeleteSuccess}
      />
      <div className='mt-3 grid h-fit grid-cols-1 gap-3'>
        {/* show the question  */}
        <Card
          className='h-fit border-none px-6 py-5 text-[#002652] shadow-none'
          data-scroll-target='true'
        >
          {editableData?.sub_questions?.length === 1 ? (
            // One part: the scenario lives on the question and the task on the
            // sub-question, so both are needed. They hold the same text when the matrix
            // file follows the template, and then only one of them is shown.
            isEditing && !cantEditMark ? (
              <>
                {!!editableData?.text?.trim() &&
                  editableData.text.trim() !==
                    (editableData?.sub_questions[0]?.text ?? '').trim() && (
                    <p className='mb-2 w-full whitespace-pre-wrap select-text'>
                      {editableData.text}
                    </p>
                  )}
                <textarea
                  className={`min-h-[300px] w-full resize-y rounded-md border p-2 whitespace-pre-wrap outline-none ${
                    isBlank(editableData?.sub_questions[0]?.text)
                      ? 'border-red-500'
                      : 'border-[#C2DEFF]'
                  }`}
                  data-invalid={
                    isBlank(editableData?.sub_questions[0]?.text)
                      ? 'true'
                      : 'false'
                  }
                  rows={3}
                  value={editableData?.sub_questions[0]?.text ?? ''}
                  onChange={(e) =>
                    setEditableData((prev) => {
                      if (!prev) return prev
                      const next = { ...prev }
                      const subQuestions = [...next.sub_questions]
                      subQuestions[0] = {
                        ...subQuestions[0],
                        text: e.target.value,
                      }
                      next.sub_questions = subQuestions
                      return next
                    })
                  }
                />
              </>
            ) : (
              <p className='w-full whitespace-pre-wrap select-text'>
                {buildSingleSubQuestionText(
                  questionDetail.text,
                  editableData?.sub_questions[0]?.text,
                ) || 'N/A'}
              </p>
            )
          ) : // Multiple sub-questions, show the original question text
          isEditing && !cantEditMark ? (
            <textarea
              className={`min-h-[300px] w-full resize-y rounded-md border p-2 outline-none ${
                isBlank(editableData?.text)
                  ? 'border-red-500'
                  : 'border-[#C2DEFF]'
              }`}
              data-invalid={isBlank(editableData?.text) ? 'true' : 'false'}
              rows={3}
              value={editableData?.text ?? ''}
              onChange={(e) =>
                setEditableData((prev) => {
                  if (!prev) return prev
                  return {
                    ...prev,
                    text: e.target.value,
                  }
                })
              }
            />
          ) : (
            <p className='w-full whitespace-pre-wrap select-text'>
              {questionDetail.text ?? 'N/A'}
            </p>
          )}
        </Card>

        {/* list question and mark schema */}
        <div className='flex flex-col gap-3'>
          {editableData?.sub_questions?.length === 1 ? (
            // If only one sub-question, display it as the main question
            <div className='scroll-mt-[120px]' data-question-container='true'>
              <ChildQuestionContainer
                chapter={editableData?.sub_questions?.[0]?.chapters?.[0] ?? ''}
                title=''
                triggerContent={<></>}
                defaultOpen={true}
                hideToggle={false}
                content={
                  <QuestionSchema
                    cantEditMark={cantEditMark}
                    isParagraph={true}
                    isEditing={isEditing}
                    data={
                      editableData
                        ? {
                            indicative_content:
                              editableData.sub_questions[0].indicative_content,
                            rubric: '',
                          }
                        : undefined
                    }
                    scheme={editableData.sub_questions[0].marking_scheme}
                    onChange={(updated: {
                      indicative_content?: string
                      scheme?: MarkingSchemeType[]
                    }) => {
                      setEditableData((prev) => {
                        if (!prev) return prev
                        const next: QuestionDetailType = {
                          ...prev,
                        }
                        if (updated.indicative_content !== undefined) {
                          const subQuestions = [...next.sub_questions]
                          subQuestions[0] = {
                            ...subQuestions[0],
                            indicative_content: updated.indicative_content,
                          }
                          next.sub_questions = subQuestions
                        }
                        if (updated.scheme) {
                          const subQuestions = [...next.sub_questions]
                          subQuestions[0] = {
                            ...subQuestions[0],
                            marking_scheme: updated.scheme,
                          }
                          next.sub_questions = subQuestions
                        }
                        return next
                      })
                    }}
                  />
                }
                time={editableData.sub_questions[0].time_limit}
                score={editableData.sub_questions[0].marks}
                isEditing={isEditing}
                editableChapter={{
                  value: editableData.sub_questions[0].chapters ?? [],
                  chapterId: editableData.sub_questions[0].chapter_id,
                  onChange: (chapterId: number, chapterName: string) => {
                    setEditableData((prev) => {
                      if (!prev) return prev
                      const next: QuestionDetailType = { ...prev }
                      const subQuestions = [...next.sub_questions]
                      subQuestions[0] = {
                        ...subQuestions[0],
                        chapters: [chapterName],
                        chapter_id: chapterId,
                      }
                      next.sub_questions = subQuestions
                      return next
                    })
                  },
                  options: chapters ?? [],
                  canEdit: !questionDetail.in_pt,
                }}
                editableScore={{
                  value: Number(editableData.sub_questions[0].marks) || 0,
                  onChange: (value: number) => {
                    setEditableData((prev) => {
                      if (!prev) return prev
                      const next: QuestionDetailType = { ...prev }
                      const subQuestions = [...next.sub_questions]
                      subQuestions[0] = {
                        ...subQuestions[0],
                        marks: value,
                      }
                      next.sub_questions = subQuestions
                      return next
                    })
                  },
                  invalid: (() => {
                    const item = editableData.sub_questions[0]
                    const maxAllowed = (() => {
                      if (
                        !item.marking_scheme ||
                        item.marking_scheme.length === 0
                      )
                        return undefined
                      const maxima: number[] = []
                      for (const sch of item.marking_scheme) {
                        if ((sch.mark_range ?? '').trim().length === 0) {
                          continue
                        }
                        const value = (sch.mark_range || '').trim()
                        const parts = value.split('-')
                        if (
                          parts.length === 2 &&
                          /^\d+$/.test(parts[1].trim())
                        ) {
                          maxima.push(parseInt(parts[1].trim(), 10))
                        }
                      }
                      if (maxima.length === 0) return undefined
                      const m = Math.max(...maxima)
                      return Number.isFinite(m) ? m : undefined
                    })()
                    return (
                      Number(item.marks) < 1 ||
                      (typeof maxAllowed === 'number' &&
                        maxAllowed > 0 &&
                        Number(item.marks) !== maxAllowed)
                    )
                  })(),
                  canEdit: !cantEditMark,
                }}
                editableTime={{
                  value: Number(editableData.sub_questions[0].time_limit) || 0,
                  onChange: (value: number) => {
                    setEditableData((prev) => {
                      if (!prev) return prev
                      const next: QuestionDetailType = { ...prev }
                      const subQuestions = [...next.sub_questions]
                      subQuestions[0] = {
                        ...subQuestions[0],
                        time_limit: value,
                      }
                      next.sub_questions = subQuestions
                      return next
                    })
                  },
                  invalid:
                    !Number.isFinite(
                      Number(editableData.sub_questions[0].time_limit)
                    ) || Number(editableData.sub_questions[0].time_limit) < 1,
                  canEdit: !cantEditMark,
                }}
              />
            </div>
          ) : (
            // Multiple sub-questions, display each one
            editableData?.sub_questions?.map(
              (item: SubQuestion, index: number) => {
                const maxAllowed = (() => {
                  if (!item.marking_scheme || item.marking_scheme.length === 0)
                    return undefined
                  const maxima: number[] = []
                  for (const sch of item.marking_scheme) {
                    // Only validate entries with mark ranges
                    if ((sch.mark_range ?? '').trim().length === 0) {
                      continue
                    }
                    const value = (sch.mark_range || '').trim()
                    // Only allow min-max format, not single numbers
                    const parts = value.split('-')
                    if (parts.length === 2 && /^\d+$/.test(parts[1].trim())) {
                      maxima.push(parseInt(parts[1].trim(), 10))
                    }
                  }
                  if (maxima.length === 0) return undefined
                  const m = Math.max(...maxima)
                  return Number.isFinite(m) ? m : undefined
                })()
                return (
                  <div
                    key={index}
                    className='scroll-mt-[120px]'
                    data-question-container='true'
                  >
                    <ChildQuestionContainer
                      chapter={item?.chapters?.[0] ?? ''}
                      title=''
                      triggerContent={
                        <div
                          onMouseDown={
                            isEditing ? (e) => e.stopPropagation() : undefined
                          }
                          onClick={
                            isEditing ? (e) => e.stopPropagation() : undefined
                          }
                          onFocus={
                            isEditing ? (e) => e.stopPropagation() : undefined
                          }
                          onKeyDown={
                            isEditing ? (e) => e.stopPropagation() : undefined
                          }
                        >
                          {isEditing && !cantEditMark ? (
                            <textarea
                              className='mt-2 min-h-[80px] w-full resize-y rounded-md border border-[#C2DEFF] p-2 text-[#002652] outline-none'
                              rows={3}
                              value={item.text}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                setEditableData((prev) => {
                                  if (!prev) return prev
                                  const next: QuestionDetailType = {
                                    ...prev,
                                  }
                                  const subQuestions = [...next.sub_questions]
                                  subQuestions[index] = {
                                    ...subQuestions[index],
                                    text: e.target.value,
                                  }
                                  next.sub_questions = subQuestions
                                  return next
                                })
                              }}
                            />
                          ) : (
                            <p className='mt-2 w-full text-left whitespace-pre-wrap text-[#002652] select-text'>
                              {item.text ?? 'N/A'}
                            </p>
                          )}
                        </div>
                      }
                      content={
                        <QuestionSchema
                          cantEditMark={cantEditMark}
                          isParagraph={false}
                          isEditing={isEditing}
                          data={
                            editableData
                              ? {
                                  indicative_content: item.indicative_content,
                                  rubric: '',
                                }
                              : undefined
                          }
                          scheme={item.marking_scheme}
                          onChange={(updated: {
                            indicative_content?: string
                            scheme?: MarkingSchemeType[]
                          }) => {
                            setEditableData((prev) => {
                              if (!prev) return prev
                              const next: QuestionDetailType = {
                                ...prev,
                              }
                              if (updated.indicative_content !== undefined) {
                                const subQuestions = [...next.sub_questions]
                                subQuestions[index] = {
                                  ...subQuestions[index],
                                  indicative_content:
                                    updated.indicative_content,
                                }
                                next.sub_questions = subQuestions
                              }
                              if (updated.scheme) {
                                const subQuestions = [...next.sub_questions]
                                subQuestions[index] = {
                                  ...subQuestions[index],
                                  marking_scheme: updated.scheme,
                                }
                                next.sub_questions = subQuestions
                              }
                              return next
                            })
                          }}
                        />
                      }
                      time={item.time_limit}
                      score={item.marks}
                      isEditing={isEditing}
                      editableChapter={{
                        value: item.chapters ?? [],
                        chapterId: item.chapter_id,
                        onChange: (chapterId: number, chapterName: string) => {
                          setEditableData((prev) => {
                            if (!prev) return prev
                            const next: QuestionDetailType = { ...prev }
                            const subQuestions = [...next.sub_questions]
                            subQuestions[index] = {
                              ...subQuestions[index],
                              chapters: [chapterName],
                              chapter_id: chapterId,
                            }
                            next.sub_questions = subQuestions
                            return next
                          })
                        },
                        options: chapters ?? [],
                        canEdit: !questionDetail.in_pt,
                      }}
                      editableScore={{
                        value: Number(item.marks) || 0,
                        onChange: (value: number) => {
                          setEditableData((prev) => {
                            if (!prev) return prev
                            const next: QuestionDetailType = { ...prev }
                            const subQuestions = [...next.sub_questions]
                            subQuestions[index] = {
                              ...subQuestions[index],
                              marks: value,
                            }
                            next.sub_questions = subQuestions
                            return next
                          })
                        },
                        invalid:
                          Number(item.marks) < 1 ||
                          (typeof maxAllowed === 'number' &&
                            maxAllowed > 0 &&
                            Number(item.marks) !== maxAllowed),
                        canEdit: !cantEditMark,
                      }}
                      editableTime={{
                        value: Number(item.time_limit) || 0,
                        onChange: (value: number) => {
                          setEditableData((prev) => {
                            if (!prev) return prev
                            const next: QuestionDetailType = { ...prev }
                            const subQuestions = [...next.sub_questions]
                            subQuestions[index] = {
                              ...subQuestions[index],
                              time_limit: value,
                            }
                            next.sub_questions = subQuestions
                            return next
                          })
                        },
                        invalid:
                          !Number.isFinite(Number(item.time_limit)) ||
                          Number(item.time_limit) < 1,
                        canEdit: !cantEditMark,
                      }}
                    />
                  </div>
                )
              }
            )
          )}
        </div>
      </div>
    </>
  )
}

export default ParagraphQuestionDetail
